/* eslint-disable react-refresh/only-export-components */
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {HitItemCatalogue} from 'api-types/ffconnect';
import {Results} from '@orama/orama';
import {useCapture} from '@/CaptureSetup';
import {debounce} from 'lodash-es';

interface SearchContextValue {
  liveTerm: string;
  liveResults: Results<HitItemCatalogue> | undefined;
  setLive: (term: string, results?: Results<HitItemCatalogue>) => void;
  clearLive: () => void;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

/**
 * Provider de recherche partagé entre la Searchbar (navbar) et le Layout.
 * Permet d'afficher les résultats de recherche en temps réel sous la navbar (md+)
 * sans ouvrir de modale, en remplaçant le contenu de la page.
 */
export const SearchProvider: React.FC<PropsWithChildren> = ({children}) => {
  const [liveTerm, setLiveTerm] = useState('');
  const [liveResults, setLiveResults] = useState<Results<HitItemCatalogue>>();
  const capture = useCapture();
  const debounceCapture = useMemo(() => debounce(capture, 1000), [capture]);

  // Met à jour le terme recherché et les résultats associés
  const setLive = useCallback(
    (term: string, results?: Results<HitItemCatalogue>) => {
      setLiveTerm(term);
      setLiveResults(results);
      if (results) {
        debounceCapture({
          name: 'search:hits',
          props: {
            count: results.count,
            first_score: results.hits[0]?.score,
            term: term,
          },
        });
      }
    },
    [debounceCapture],
  );

  // Réinitialise la recherche (retour au contenu normal de la page)
  const clearLive = useCallback(() => {
    setLiveTerm('');
    setLiveResults(undefined);
  }, []);

  const value = useMemo(
    () => ({liveTerm, liveResults, setLive, clearLive}),
    [liveTerm, liveResults, setLive, clearLive],
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context)
    throw new Error('useSearch doit être utilisé dans un SearchProvider');
  return context;
}
